/**
 * The two cells a DataTable is made of. Both read a column definition:
 *
 *   `{ key, label, hint?, num?, className?, bold?, render?, cell?, sort?, tone? }`
 *
 * `render(row, ctx)` returns the cell's CONTENT and `Cell` draws the `<td>`;
 * `cell(row, ctx)` returns the whole `<td>` for a cell that is its own
 * component (a `NumCell` that opens a series). `num` right-aligns, `bold`
 * wraps the content, `tone(row)` adds up/down, `sort(row)` makes the header
 * sortable, `hint` is the header's tooltip.
 */

/** A header cell: label, hint, alignment, and the sort it toggles if any. */
export function Th({ col, sort, pin = false }) {
  const sortable = Boolean(sort && col.sort);
  const cls = [
    col.num ? 'num' : '',
    col.className || '',
    sortable ? 'dt-sort' : '',
    pin ? 'dt-pin' : '',
  ].filter(Boolean).join(' ');
  const active = sortable && sort.sort?.key === col.key;
  return (
    <th
      className={cls || undefined}
      title={col.hint}
      onClick={sortable ? () => sort.toggle(col.key) : undefined}
      aria-sort={active ? (sort.sort.desc ? 'descending' : 'ascending') : undefined}
    >
      {col.label}
      {sortable ? sort.arrow(col.key) : ''}
    </th>
  );
}

/**
 * A body cell. A column that renders its own `<td>` is drawn as given; one that
 * renders content gets the table's cell around it, with the caret and the
 * row's note in the first column.
 */
export function Cell({ col, row, ctx, caret = null, note = null, pin = false }) {
  if (col.cell) return col.cell(row, ctx);
  const content = col.render ? col.render(row, ctx) : row?.[col.key];
  const cls = [
    col.num ? 'num' : '',
    col.className || '',
    col.tone ? col.tone(row, ctx) || '' : '',
    pin ? 'dt-pin' : '',
  ].filter(Boolean).join(' ');
  return (
    <td className={cls || undefined}>
      {caret}
      {col.bold ? <b>{content}</b> : content}
      {note && (
        <>
          <br />
          <span className="drill-row-note">{note}</span>
        </>
      )}
    </td>
  );
}
